import {Typography} from "@mui/material";

interface AccusiteMapIconProps {
  src: string;
  sizeX: number;
  sizeY: number;
  centerImage?: boolean;
  /** Clip the marker to a circle (uses the top of taller pin assets). */
  circular?: boolean;
  label?: string;
  labelYOffset?: number;
  labelStyle?: React.CSSProperties;
  scale?: number;
  id?: string;
  /** Compass bearing in degrees (0 = north, clockwise) to rotate the image around its center. */
  rotationDeg?: number;
  /**
   * Renders a small grab handle (at `${id}-rotate-handle`) used to drive a rotate gesture.
   * The handle stays at a fixed position (top-center) regardless of the current rotation —
   * only the image itself rotates — so it's always in the same easy-to-find spot for the
   * next gesture instead of orbiting out of reach.
   */
  showRotationHandle?: boolean;
  /**
   * Apply the minimum-size floor to both axes together, measured on the icon's longest axis.
   * Without this, a non-square icon gets squashed once one axis bottoms out on the floor while
   * the other keeps shrinking.
   */
  preserveAspectRatio?: boolean;
}

const MINIMUM_SCALED_SIZE = 16;

const MapImageIcon = (props: AccusiteMapIconProps) => {
  const scale = props.scale ?? 1;
  let scaledSizeX = props.sizeX * scale;
  let scaledSizeY = props.sizeY * scale;

  // A long, thin icon stays visible on its length alone, so judge the floor by its longest
  // axis — measuring the shorter one would raise the floor far above the caller's own limit.
  const longestSide = Math.max(scaledSizeX, scaledSizeY);
  const minimumSizeBoost = longestSide > 0 ? MINIMUM_SCALED_SIZE / longestSide : 1;
  if (props.preserveAspectRatio && minimumSizeBoost > 1) {
    scaledSizeX *= minimumSizeBoost;
    scaledSizeY *= minimumSizeBoost;
  } else if (!props.preserveAspectRatio) {
    scaledSizeX = Math.max(scaledSizeX, MINIMUM_SCALED_SIZE);
    scaledSizeY = Math.max(scaledSizeY, MINIMUM_SCALED_SIZE);
  }

  if (props.circular) {
    // Diameter matches the pin head (width). Taller pin tails are clipped away.
    const diameter = scaledSizeX;
    const defaultCircularLabelStyle: React.CSSProperties = {
      color: '#ffffff',
      fontWeight: 800,
      fontSize: Math.max(10, diameter * 0.28),
      lineHeight: 1,
    };
    const labelStyle: React.CSSProperties = {
      ...defaultCircularLabelStyle,
      ...(props.labelStyle ?? {}),
    };

    return (
      <div
        id={props.id}
        style={{
          width: diameter,
          height: diameter,
          borderRadius: '50%',
          overflow: 'hidden',
          position: 'relative',
          marginBottom: props.centerImage ? -diameter / 2 : 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.45)',
          flexShrink: 0,
        }}
      >
        <img
          src={props.src}
          alt={props.label ?? ""}
          style={{
            width: diameter,
            height: scaledSizeY * (diameter / scaledSizeX),
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
        {props.label != null && props.label !== '' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none',
              // Nudge the number up toward the denser part of the icon.
              transform: 'translateY(-12%)',
            }}
          >
            <Typography component="span" style={labelStyle}>{props.label}</Typography>
          </div>
        )}
      </div>
    );
  }

  const labelStyle: React.CSSProperties = props.labelStyle ?? {fontVariant: 'h5', fontStyle: 'bold', color: 'black'};
  const rotationDeg = props.rotationDeg ?? 0;
  const handleId = props.id ? `${props.id}-rotate-handle` : undefined;
  const handleDiameter = Math.max(18, scaledSizeX * 0.16);
  return (
    <div style={{marginBottom: props.centerImage ? -scaledSizeY / 2 : 0, position: 'relative'}} id={props.id}>
      <div style={{position: "absolute", zIndex: 100, height: scaledSizeY, width: scaledSizeX, display: "flex", justifyContent: "center", alignItems: "center", top: props.labelYOffset ?? 0, pointerEvents: 'none'}}>
        <Typography style={labelStyle}>{props.label}</Typography>
      </div>
      <div style={{width: scaledSizeX, height: scaledSizeY, position: 'relative'}}>
        <img
          src={props.src}
          alt={props.label ?? ""}
          style={{width: scaledSizeX, height: scaledSizeY, display: 'block', transform: `rotate(${rotationDeg}deg)`, transformOrigin: '50% 50%'}}
        />
        {props.showRotationHandle && (
          <div
            id={handleId}
            style={{
              position: 'absolute',
              top: -Math.max(14, scaledSizeY * 0.12),
              left: '50%',
              width: handleDiameter,
              height: handleDiameter,
              marginLeft: -handleDiameter / 2,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.9)',
              border: '2px solid #1976d2',
              boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
              cursor: 'grab',
              touchAction: 'none',
              zIndex: 101,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MapImageIcon;
